import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { useDashboard } from '../../context/DashboardContext';
import { useChat } from '../../context/ChatContext';
import { useI18n } from '../../i18n';
import LogoC from '../../assets/logo_c.png';
import SnapSubmenu from './SnapSubmenu.jsx';
import './Sidebar.css';

import talkIcon from '../../assets/icons/talk.svg';
import addressIcon from '../../assets/icons/AddressBook.svg';
import minisiteIcon from '../../assets/icons/minisite_sidebar.svg';
import snapIcon from '../../assets/icons/snap.svg';
import mediaIcon from '../../assets/icons/media.svg';

const Sidebar = ({ onAddressBookToggle, onMediaToggle }) => {
    const { t } = useI18n();
    const { activeView, setActiveView, isMobileSidebarOpen, closeMobileSidebar } = useDashboard();
    const { unreadTotal, setHideSidebar } = useChat();
    const navigate = useNavigate();
    const [isSnapSubmenuOpen, setIsSnapSubmenuOpen] = useState(false);
    const [snapMenuPosition, setSnapMenuPosition] = useState({ x: 0, y: 0 });
    const snapItemRef = useRef(null);

    const menuItems = [
        { id: 'talk', icon: talkIcon, label: t('navigation.talk') },
        { id: 'address', icon: addressIcon, label: 'Address' },
        { id: 'minisite', icon: minisiteIcon, label: t('navigation.minisite') },
        { id: 'snap', icon: snapIcon, label: t('navigation.snap') },
        { id: 'media', icon: mediaIcon, label: 'Media' },
    ];

    const handleItemClick = (item, event) => {
        if (item.id === 'snap') {
            // Handle snap item click to show submenu
            event.stopPropagation();
            const rect = snapItemRef.current?.getBoundingClientRect();
            if (rect) {
                setSnapMenuPosition({ x: rect.left, y: rect.top });
                setIsSnapSubmenuOpen(true);
            }
        } else if (item.id === 'address') {
            onAddressBookToggle();
        } else if (item.id === 'media') {
            onMediaToggle();
        } else if (item.route) {
            navigate(item.route);
        } else {
            if (item.id === 'talk') setHideSidebar(false);
            setActiveView(item.id);
        }
    };

    const closeSnapSubmenu = () => {
        setIsSnapSubmenuOpen(false);
    };

    const handleLogoClick = () => {
        closeSnapSubmenu();
        setActiveView('dashboard');
    };

    // Below --bp-tablet-lg (1024px) the sidebar renders as a floating overlay
    // drawer (Responsive-Mobile-Plan.html Phase 1) instead of a permanently
    // docked column; isMobileSidebarOpen/closeMobileSidebar come from
    // DashboardContext (decided in Phase 0) and have no effect above 1024px.
    const handleItemClickAndClose = (item, event) => {
        handleItemClick(item, event);
        if (item.id !== 'snap') closeMobileSidebar();
    };

    return (
        <>
            <div
                className={`sidebar-backdrop ${isMobileSidebarOpen ? 'sidebar-backdrop--visible' : ''}`}
                onClick={closeMobileSidebar}
                aria-hidden="true"
            />
            <div className={`sidebar ${isMobileSidebarOpen ? 'sidebar--open' : ''}`}>
                {/* Once open, the drawer sits above the header (z-index above
                    --z-header) and visually covers the header's own hamburger
                    button at the same position, making it unreachable to close
                    the drawer again. A close button inside the drawer itself
                    is the standard mobile pattern and the only way to close
                    without relying solely on the backdrop tap. Mobile-only via
                    CSS (hidden above 1024px, alongside the rest of the drawer
                    behavior). */}
                <button
                    type="button"
                    className="sidebar-close-btn"
                    onClick={closeMobileSidebar}
                    aria-label={t('common.actions.close')}
                >
                    <FaTimes />
                </button>
                <button
                    type="button"
                    className="sidebar-logo"
                    onClick={handleLogoClick}
                    aria-label={t('navigation.back_to_dashboard')}
                >
                    <img src={LogoC} alt="" />
                </button>
                <div className="sidebar-menu">
                    {menuItems.map((item) => (
                        <div
                            key={item.id}
                            ref={item.id === 'snap' ? snapItemRef : null}
                            className={`sidebar-item ${activeView === item.id ? 'active' : ''}`}
                            onClick={(event) => handleItemClickAndClose(item, event)}
                        >
                            <div className="sidebar-icon">
                                <img src={item.icon} alt={item.label} width={32} height={32} />
                                {item.id === 'talk' && unreadTotal > 0 && (
                                    <span className="sidebar-badge">{unreadTotal > 99 ? '99+' : unreadTotal}</span>
                                )}
                            </div>
                            <span className="sidebar-label">{item.label}</span>
                        </div>
                    ))}
                </div>
                <SnapSubmenu
                    isOpen={isSnapSubmenuOpen}
                    onClose={closeSnapSubmenu}
                    position={snapMenuPosition}
                />
            </div>
        </>
    );
};

export default Sidebar;
